import { useState, useRef, useCallback, useEffect } from "react";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Trash, Upload, Image as ImageIcon, DotsSixVertical } from "@phosphor-icons/react";

export default function PhotoUploader({ photos = [], onPhotoUploaded, onRemovePhoto, onReorder }) {
  const [uploading, setUploading] = useState(false);
  const [photoUrls, setPhotoUrls] = useState({});
  const fileInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);

  const loadPhoto = useCallback(async (pid) => {
    try {
      const resp = await axios.get(`${API}/photos/${pid}`, {
        responseType: "blob"
      });
      const url = URL.createObjectURL(resp.data);
      setPhotoUrls(prev => ({ ...prev, [pid]: url }));
    } catch {}
  }, []);

  useEffect(() => {
    photos.forEach(pid => {
      if (!photoUrls[pid]) loadPhoto(pid);
    });
  }, [photos, loadPhoto, photoUrls]);

  const uploadFile = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    setUploading(true);
    try {
      const { data } = await axios.post(`${API}/photos/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      onPhotoUploaded(data.id);
      setTimeout(() => loadPhoto(data.id), 200);
      toast.success(`Photo uploaded: ${file.name}`);
    } catch {
      toast.error("Failed to upload photo");
    } finally {
      setUploading(false);
    }
  };

  const handleFiles = (files) => {
    Array.from(files).forEach(file => {
      if (file.type.startsWith("image/")) {
        uploadFile(file);
      } else {
        toast.error(`${file.name} is not an image`);
      }
    });
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleDelete = async (pid) => {
    try {
      await axios.delete(`${API}/photos/${pid}`);
      onRemovePhoto(pid);
      setPhotoUrls(prev => {
        const next = { ...prev };
        if (next[pid]) {
          URL.revokeObjectURL(next[pid]);
          delete next[pid];
        }
        return next;
      });
    } catch {
      toast.error("Failed to remove photo");
    }
  };

  // Drag-and-drop reorder handlers
  const handleDragStart = (e, idx) => {
    setDragIdx(idx);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", idx.toString());
  };

  const handleDragOverItem = (e, idx) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverIdx(idx);
  };

  const handleDropItem = (e, dropIdx) => {
    e.preventDefault();
    e.stopPropagation();
    const fromIdx = dragIdx;
    setDragIdx(null);
    setDragOverIdx(null);

    if (fromIdx === null || fromIdx === dropIdx) return;

    const reordered = [...photos];
    const [moved] = reordered.splice(fromIdx, 1);
    reordered.splice(dropIdx, 0, moved);

    if (onReorder) {
      onReorder(reordered);
    }
  };

  const handleDragEnd = () => {
    setDragIdx(null);
    setDragOverIdx(null);
  };

  return (
    <div data-testid="photo-uploader">
      {/* Drop zone */}
      <div
        className={`border-2 border-dashed rounded-md p-6 text-center cursor-pointer transition-colors ${
          dragOver ? "border-blue-500 bg-blue-50" : "border-slate-300 bg-slate-50 hover:border-blue-400"
        }`}
        onClick={() => fileInputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleFileDrop}
        data-testid="photo-dropzone"
      >
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          accept="image/*"
          multiple
          onChange={(e) => handleFiles(e.target.files)}
        />
        {uploading ? (
          <div className="flex flex-col items-center gap-2">
            <div className="w-6 h-6 border-2 border-blue-900 border-t-transparent rounded-full animate-spin" />
            <p className="text-sm text-slate-500">Uploading...</p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <Upload size={28} weight="light" className="text-slate-400" />
            <p className="text-sm text-slate-600">
              <span className="font-semibold text-blue-900">Click to upload</span> or drag and drop
            </p>
            <p className="text-xs text-slate-400">Images auto-compressed</p>
          </div>
        )}
      </div>

      {/* Photo grid with drag-and-drop reorder */}
      {photos.length > 0 && (
        <>
          {onReorder && photos.length > 1 && (
            <p className="text-xs text-slate-400 mt-2 mb-1">Drag photos to reorder</p>
          )}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2" data-testid="photo-grid">
            {photos.map((pid, idx) => (
              <div
                key={pid}
                draggable={!!onReorder && photos.length > 1}
                onDragStart={(e) => handleDragStart(e, idx)}
                onDragOver={(e) => handleDragOverItem(e, idx)}
                onDrop={(e) => handleDropItem(e, idx)}
                onDragEnd={handleDragEnd}
                className={`relative group aspect-video bg-slate-100 rounded-md overflow-hidden border transition-all ${
                  dragOverIdx === idx && dragIdx !== idx
                    ? "border-blue-500 ring-2 ring-blue-200"
                    : dragIdx === idx
                      ? "opacity-50 border-slate-300"
                      : "border-slate-200"
                }`}
              >
                {photoUrls[pid] ? (
                  <img src={photoUrls[pid]} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <ImageIcon size={24} className="text-slate-300" />
                  </div>
                )}
                {/* Drag handle */}
                {onReorder && photos.length > 1 && (
                  <div className="absolute top-1.5 left-1.5 bg-white/80 rounded p-0.5 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing">
                    <DotsSixVertical size={14} className="text-slate-500" />
                  </div>
                )}
                {/* Order number badge */}
                <div className="absolute bottom-1.5 left-1.5 bg-black/60 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded">
                  {idx + 1}
                </div>
                {/* Delete button */}
                <button
                  className="absolute top-1.5 right-1.5 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={(e) => { e.stopPropagation(); handleDelete(pid); }}
                  data-testid={`delete-photo-${pid}`}
                >
                  <Trash size={12} />
                </button>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
